import { ComponentType, lazy } from "react";

// Detects the "module gone" failure mode that happens when a stale tab
// references a chunk hash that the latest deploy pruned from S3. The error
// shape varies by browser; match the ones we've seen in the wild.
function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    err.name === "ChunkLoadError"
  );
}

// Avoid bouncing the user into an infinite reload loop if the new index.html
// is *also* broken (e.g. CDN serving 404s). One reload per page-load is
// enough; the second time we re-throw and let React Router's error boundary
// surface the failure.
const RELOAD_MARK = "__chunk_reload_attempted__";

function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_MARK)) return false;
    sessionStorage.setItem(RELOAD_MARK, "1");
  } catch {
    // sessionStorage may be unavailable (private mode, sandboxed iframe).
    // Best-effort: still reload — at worst the user sees the error twice.
  }
  window.location.reload();
  return true;
}

// `unknown` props lets the wrapper accept any lazy-loaded component shape
// without committing to a fixed prop signature — the consumer's <Component>
// usage type-checks against the actual export.
export function lazyWithReload<T extends ComponentType<unknown>>(
  loader: () => Promise<{ default: T }>
) {
  return lazy(() =>
    loader().catch((err) => {
      if (isChunkLoadError(err) && reloadOnce()) {
        // Hold the promise so React doesn't render an error while the
        // browser is navigating to the fresh page.
        return new Promise<{ default: T }>(() => {
          /* never resolves; reload navigates away */
        });
      }
      throw err;
    })
  );
}
